import styled from '@emotion/styled';
import { Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../../../entities/ConnectedUserEntity';
import { FavorisEntity } from '../../../../../entities/favoris.entity';
import { TitreEntity } from '../../../../../entities/titre.entity';
import { PRIMARY, YELLOW } from '../../../../../shared/colors';
import { dateFormatterNoTime } from '../../../../shared/Table/cellFormatter';
import { getUserFavoris } from '../../../network';

const Container = styled.div`
  color: var(--mt-text);
  height: 100%;
  box-sizing: border-box;
  padding: 8px 10px 10px;
  overflow-x: auto;
  overflow-y: auto;
  font-family: Poppins-R;

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-family: Poppins-B;
    font-size: 11px;
    font-weight: normal;
    text-transform: uppercase;
  }

  .monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .monitor-title {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .monitor-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .refresh-icon {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background-color: var(--mt-surface);
    cursor: pointer;
  }

  .empty-state {
    display: grid;
    min-height: 180px;
    place-items: center;
    color: var(--mt-muted);
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-table-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-table-text);
  }
`;

export const Favoris = ({
  getTitre,
}: {
  getTitre: (b: TitreEntity, ope: '1' | '2') => void;
}) => {
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const [favoris, setFavoris] = useState<FavorisEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getUserFavoris(connectedUser.id).then(({ data }) => {
      setFavoris(data);
      setIsLoading(false);
    });
  };

  return (
    <Container>
      <div
        className='monitor-toolbar'
      >
        <span className='monitor-title'>Liste de surveillance</span>
        <div className='monitor-actions'>
          <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
          <Tooltip title='Rafraichir' placement='leftTop' color={YELLOW}>
            <span className='refresh-icon' onClick={fetchData}>
              <FiRefreshCcw />
            </span>
          </Tooltip>
        </div>
      </div>
      {favoris.length === 0 ? (
        <div className='empty-state'>Aucun favori disponible</div>
      ) : (
        <Table
          columns={COLUMNS}
          dataSource={favoris}
          rowSelection={{
            type: 'radio',
            onChange: (selectedRowKeys, selectedRows) => {
              getTitre(selectedRows[0].titre, '1');
            },
          }}
          rowClassName={(row, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
          rowKey='id'
          size='small'
          style={{ zoom: 0.8 }}
          pagination={false}
          showSorterTooltip={false}
        />
      )}
    </Container>
  );
};

const COLUMNS = [
  {
    title: 'Pays',
    render: (cell: any, row: FavorisEntity) => row.titre.pays,
    sorter: (a: FavorisEntity, b: FavorisEntity) =>
      a.titre.pays.localeCompare(b.titre.pays),
  },
  {
    title: 'Type',
    render: (cell: any, row: FavorisEntity) => row.titre.instrument,
    sorter: (a: FavorisEntity, b: FavorisEntity) =>
      a.titre.instrument.localeCompare(b.titre.instrument),
  },
  {
    title: 'CODE',
    sorter: (a: FavorisEntity, b: FavorisEntity) =>
      a.titre.code.localeCompare(b.titre.code),
    render: (cell: any, row: FavorisEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.titre.code}
      </div>
    ),
  },
  {
    title: 'Date de maturation',
    render: (cell: any, row: FavorisEntity) => row.titre.dateMaturite,
    sorter: (a: FavorisEntity, b: FavorisEntity) =>
      a.titre.dateMaturite.localeCompare(b.titre.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: FavorisEntity, b: FavorisEntity) =>
      a.titre.tauxMarginal - b.titre.tauxMarginal,
    render: (cell: any, row: FavorisEntity) => (
      <>{Number(row.titre.tauxMarginal)}</>
    ),
  },
  {
    title: 'Date ajout',
    dataIndex: 'created_at',
    render: dateFormatterNoTime,
    sorter: (a: FavorisEntity, b: FavorisEntity) =>
      a.created_at.localeCompare(b.created_at),
  },
];
